"use client";

import { usePathname, useRouter } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { useTheme } from "next-themes";
import {
  Menu,
  Globe,
  Moon,
  Sun,
  BookOpen,
  User,
  Key,
  LogOut,
  ChevronDown,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/contexts/sidebar-context";
import { useAuth } from "@/contexts/auth-context";
import { useCommandPalette } from "@/contexts/command-palette-context";
import { CapabilityGate } from "@/components/capability-gate";
import { cn } from "@/lib/utils";
import { governancePageTitle } from "@/lib/governance-navigation";

const pageKeyMap: Record<string, string> = {
  "": "home",
  analytics: "analytics",
  runs: "runs",
  logs: "logs",
  library: "library",
  prompts: "prompts",
  configs: "configs",
  profile: "profile",
  "api-keys": "apiKeys",
  domains: "domains",
  tenants: "tenants",
  "domain-access": "domainAccess",
};

export function Navbar() {
  const pathname = usePathname();
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("nav");
  const tCommon = useTranslations("common");
  const { theme, setTheme } = useTheme();
  const { toggleMobile } = useSidebar();
  const { user, isAuthenticated, logout } = useAuth();
  const { setOpen } = useCommandPalette();
  const isRTL = locale === "ar";

  // Get current page key from pathname
  const pathSegments = pathname.split("/").filter(Boolean);
  const pageSegment = pathSegments[1] || "";
  const pageKey = pageKeyMap[pageSegment] || "home";
  const governanceTitle = governancePageTitle("/" + pageSegment, locale);

  const switchLocale = (newLocale: string) => {
    const newPath = pathname.replace(`/${locale}`, `/${newLocale}`);
    router.push(newPath);
  };

  const handleLogout = async () => {
    await logout();
    router.push(`/${locale}`);
  };

  return (
    <nav className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4">
      {/* Mobile menu toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={toggleMobile}
      >
        <Menu className="h-5 w-5" />
        <span className="sr-only">{tCommon("toggleSidebar")}</span>
      </Button>

      {/* Page title */}
      <div className="flex items-center gap-2">
        <span className="font-medium text-foreground">
          {governanceTitle ? governanceTitle : pageKey === "profile"
            ? tCommon("profile")
            : pageKey === "apiKeys"
            ? tCommon("apiKeys")
            : t(pageKey as "home" | "analytics" | "runs" | "logs" | "library" | "prompts" | "configs")}
        </span>
      </div>

      {/* Cmd+K search hint */}
      <button
        onClick={() => setOpen(true)}
        className="hidden md:flex items-center gap-2 h-8 px-3 rounded-lg border border-border bg-slate-50 dark:bg-slate-900 text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground transition-all"
      >
        <Search className="h-3.5 w-3.5" />
        <span>{tCommon("search")}</span>
        <div className="flex items-center gap-0.5 ml-2 opacity-60">
          <kbd className="px-1.5 py-0.5 rounded text-[10px] font-mono border bg-background">⌘</kbd>
          <kbd className="px-1.5 py-0.5 rounded text-[10px] font-mono border bg-background">K</kbd>
        </div>
      </button>

      {/* Spacer */}
      <div className={cn("flex-1", isRTL && "order-first")} />

      {/* Actions */}
      <div className="flex items-center gap-2">
        {/* Language switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" title={tCommon("switchLanguage")}>
              <Globe className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={isRTL ? "start" : "end"}>
            <DropdownMenuItem onClick={() => switchLocale("en")}>
              <span className="mr-2">🇺🇸</span>
              English
              {locale === "en" && <span className="ms-auto">✓</span>}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => switchLocale("ar")}>
              <span className="mr-2">🇸🇦</span>
              العربية
              {locale === "ar" && <span className="ms-auto">✓</span>}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          title={tCommon("toggleTheme")}
        >
          <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          <span className="sr-only">{tCommon("toggleTheme")}</span>
        </Button>

        {/* Documentation */}
        <Button variant="ghost" size="icon" asChild title={tCommon("documentation")}>
          <a href="/docs" target="_blank" rel="noopener noreferrer">
            <BookOpen className="h-5 w-5" />
          </a>
        </Button>

        {/* User menu (when authenticated) */}
        {isAuthenticated && user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 px-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                  <User className="h-4 w-4" />
                </div>
                <span className="hidden md:inline text-sm font-medium">
                  {user.first_name} {user.last_name}
                </span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={isRTL ? "start" : "end"} className="w-48">
              <DropdownMenuItem asChild>
                <a
                  href={`/${locale}/profile`}
                  className="flex items-center gap-2"
                >
                  <User className="h-4 w-4 text-primary" />
                  {tCommon("profile")}
                </a>
              </DropdownMenuItem>
              {/* Gated on api_keys.manage_own, matching sidebar.tsx's
                  requiredCapability treatment (Phase 6 of
                  [[rbac-permission-overhaul-initiative]]) — this dropdown
                  previously showed "API Keys" unconditionally to every
                  authenticated user regardless of whether an admin had
                  explicitly denied them that permission, so clicking it
                  led straight to a real backend 403 with no warning. */}
              <CapabilityGate capability="api_keys.manage_own">
                <DropdownMenuItem asChild>
                  <a
                    href={`/${locale}/api-keys`}
                    className="flex items-center gap-2"
                  >
                    <Key className="h-4 w-4 text-green-500" />
                    {tCommon("apiKeys")}
                  </a>
                </DropdownMenuItem>
              </CapabilityGate>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {tCommon("signOut")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </nav>
  );
}
